import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MessageCircle, User, Layers, Loader2, Sparkles, ArrowLeft, Flame, Info,
} from "lucide-react";
import { API } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { toast } from "sonner";
import { StreamingProgress } from "../components/StreamingProgress";

/**
 * Window 1 — "Oceń swój profil" (self, FREE).
 * Description-only self analysis: NO photo upload, no payment required.
 */
export default function Analyze() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const pl = lang === "pl";

  const [mode, setMode] = useState("profile");
  const [text, setText] = useState("");
  const [noPressure, setNoPressure] = useState(false);
  const [moreFlirty, setMoreFlirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [streamPhase, setStreamPhase] = useState("idle");

  const modes = [
    { key: "profile", icon: User, label: pl ? "Mój profil / bio" : "My profile / bio", desc: pl ? "Oceń swój opis" : "Rate your bio" },
    { key: "conversation", icon: MessageCircle, label: pl ? "Moja rozmowa" : "My conversation", desc: pl ? "Wklej swoje wiadomości" : "Paste your messages" },
    { key: "full", icon: Layers, label: pl ? "Pełna analiza" : "Full analysis", desc: pl ? "Wszystko o Tobie" : "Everything about you" },
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) {
      toast.error(pl ? "Wpisz opis swojego profilu" : "Enter your profile description");
      return;
    }
    setLoading(true);
    setStreamText("");
    setStreamPhase("starting");
    try {
      const fd = new FormData();
      fd.append("mode", mode);
      fd.append("contact_name", "");
      fd.append("text", text);
      fd.append("language", lang);
      fd.append("no_pressure", String(noPressure));
      fd.append("more_flirty", String(moreFlirty));

      const token = localStorage.getItem("dc_token");
      const res = await fetch(`${API}/analyze/stream`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const detail = await res.text();

        if (res.status === 402 || detail.includes("payment_required")) {
          throw new Error(
            pl
              ? "Brak kredytu. Zwykły raport kosztuje 1 kredyt."
              : "Not enough credits. A standard report costs 1 credit."
          );
        }

        throw new Error(detail || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let reportId = null;
      let accumulatedText = "";

      setStreamPhase("streaming");
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const block = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = block.split("\n");
          let event = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
          if (!data) continue;
          let payload;

          try {
            payload = JSON.parse(data);
          } catch {
            continue;
          }

          if (event === "chunk" && payload.text) {
            accumulatedText += payload.text;
            setStreamText(accumulatedText);
          } else if (event === "done") {
            reportId = payload.id || payload.report_id || null;
            setStreamPhase("done");
          } else if (event === "error") {
            throw new Error(payload.detail || "AI error");
          }
        }
      }

      if (reportId) navigate(`/report/${reportId}`);
      else throw new Error("Stream finished without report id");
    } catch (err) {
      toast.error(err.message || t("analysisError"));
      setStreamPhase("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[360px] overflow-hidden">
        <div className="absolute -top-24 -left-24 h-[26rem] w-[26rem] rounded-full bg-gradient-to-br from-[#ff4458]/20 via-[#fd267a]/15 to-transparent blur-3xl" />
      </div>

      <button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard"
        className="mb-6 inline-flex items-center gap-2 text-sm text-[#a8adbd] transition-colors hover:text-white">
        <ArrowLeft className="h-4 w-4" />{t("backToDashboard")}
      </button>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="inline-flex items-center gap-2 rounded-full border border-[#32d583]/30 bg-[#32d583]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#32d583]">
          <Flame className="h-3.5 w-3.5" />{pl ? "Za darmo" : "Free"}
        </div>
        <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl" data-testid="self-analyze-title">
          {pl ? "Oceń swój profil" : "Rate your own profile"}
        </h1>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-[#a8adbd]">
          {pl ? "Analiza dotycząca tylko Ciebie — na podstawie opisu. Bez zdjęć, bez opłat."
              : "Analysis about you only — based on your description. No photos, no payment."}
        </p>
      </motion.div>

      <div className="mt-5 flex items-start gap-2 rounded-2xl border border-[#222636] bg-[#181b24] p-4 text-xs text-[#a8adbd]" data-testid="self-free-note">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#fdb022]" />
        <span>{pl ? "Chcesz ocenić inną osobę (zdjęcia + opis + profil psychologiczny)? To funkcja premium — "
                  : "Want to rate another person (photos + description + psychological profile)? That's premium — "}
          <button onClick={() => navigate("/analyze/premium")} className="font-semibold dc-gradient-text" data-testid="self-to-premium-link">
            {pl ? "przejdź tutaj" : "go here"}
          </button>.
        </span>
      </div>

      <form onSubmit={onSubmit} className="mt-8 space-y-6" data-testid="analyze-form">
        <section className="dc-card p-6 sm:p-8">
          <h2 className="mb-4 font-display text-lg font-semibold text-white">{t("analysisMode")}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {modes.map((m) => {
              const Icon = m.icon;
              const active = mode === m.key;
              return (
                <button type="button" key={m.key} onClick={() => setMode(m.key)} data-testid={`mode-${m.key}`}
                  className={`group relative overflow-hidden rounded-2xl border p-4 text-left transition-all ${
                    active ? "border-[#ff4458] bg-gradient-to-br from-[#ff4458]/10 to-[#ff6036]/5 shadow-[0_8px_24px_rgba(253,38,122,0.2)]" : "border-[#222636] bg-[#0f1117] hover:border-[#ff4458]/40"
                  }`}>
                  <Icon className={`mb-2 h-5 w-5 ${active ? "text-[#ff4458]" : "text-[#a8adbd] group-hover:text-white"}`} />
                  <div className="font-display text-sm font-semibold text-white">{m.label}</div>
                  <div className="mt-1 text-xs text-[#a8adbd]">{m.desc}</div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="dc-card p-6 sm:p-8">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#a8adbd]">
            {pl ? "Opis Twojego profilu" : "Your profile description"}
          </label>
          <textarea data-testid="context-text-area" value={text} onChange={(e) => setText(e.target.value)}
            placeholder={pl ? "Wklej swoje bio z aplikacji randkowej lub opisz siebie: zainteresowania, styl wiadomości, czego szukasz…"
                            : "Paste your dating bio or describe yourself: interests, message style, what you're looking for…"}
            rows={8}
            className="w-full resize-y rounded-2xl border border-[#222636] bg-[#0f1117] px-4 py-3 text-white outline-none transition placeholder:text-[#a8adbd]/40 focus:border-[#ff4458] focus:ring-2 focus:ring-[#ff4458]/20" />
        </section>

        <section className="dc-card p-6 sm:p-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Toggle testId="toggle-no-pressure" checked={noPressure} onChange={setNoPressure} label={t("noPressure")} hint={t("noPressureHint")} />
            <Toggle testId="toggle-more-flirty" checked={moreFlirty} onChange={setMoreFlirty} label={t("moreFlirty")} hint={t("moreFlirtyHint")} />
          </div>
        </section>

        <button type="submit" disabled={loading} data-testid="analyze-submit-button"
          className="flex w-full items-center justify-center gap-2 rounded-full dc-gradient-bg px-6 py-4 font-display text-base font-semibold text-white shadow-[0_12px_36px_rgba(253,38,122,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_44px_rgba(253,38,122,0.5)] disabled:cursor-not-allowed disabled:opacity-60">
          {loading ? (<><Loader2 className="h-5 w-5 animate-spin" /><span>{streamPhase === "streaming" ? t("streamingProgress") : t("analyzing")}</span></>)
                   : (<><Sparkles className="h-5 w-5" /><span>{pl ? "Oceń mój profil" : "Rate my profile"}</span></>)}
        </button>

        {loading && (
          <section className="dc-card relative overflow-hidden p-5 sm:p-6" data-testid="stream-preview">
            <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-gradient-to-br from-[#ff4458]/25 to-transparent blur-2xl" />
            <div className="relative">
              <StreamingProgress phase={streamPhase} charsStreamed={streamText.length} />
              {streamText && (
                <pre className="mt-4 max-h-32 overflow-auto rounded-xl bg-[#0f1117] p-3 font-mono text-[10.5px] leading-relaxed text-[#a8adbd]" data-testid="stream-text">
                  {streamText.slice(-1000)}
                </pre>
              )}
            </div>
          </section>
        )}
      </form>
    </div>
  );
}

function Toggle({ checked, onChange, label, hint, testId }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testId}
      className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-all ${
        checked ? "border-[#ff4458] bg-[#ff4458]/5" : "border-[#222636] bg-[#0f1117] hover:border-[#ff4458]/40"
      }`}>
      <div className={`mt-0.5 flex h-5 w-9 flex-shrink-0 items-center rounded-full p-0.5 transition-colors ${checked ? "dc-gradient-bg" : "bg-[#222636]"}`}>
        <div className={`h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0"}`} />
      </div>
      <div>
        <div className="font-display text-sm font-semibold text-white">{label}</div>
        <div className="text-xs text-[#a8adbd]">{hint}</div>
      </div>
    </button>
  );
}
